import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Mail,
  Edit2,
  FileText,
  Clock,
  XCircle
} from 'lucide-react';
import emailTemplateService from '@/services/email-template.service';
import { EmailTemplateFilterDto } from '@/types/email-template.types';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';
import Pagination from '@/components/ui/Pagination';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import SearchInputDebounced from '@/components/ui/SearchInputDebounced';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { EMAIL_TEMPLATE_PERMISSION } from '@/constants/constant-permissions';

// Uses the first valid date given; shows "—" when none (e.g. records inserted by script without timestamps).
const formatDate = (...date_values: Array<string | undefined | null>): string => {
  const valid_date = date_values
    .map((value) => (value ? new Date(value) : null))
    .find((date): date is Date => !!date && !isNaN(date.getTime()));
  if (!valid_date) return '—';
  return valid_date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export default function EmailTemplateList() {
  usePermissionRouteAccess([
    EMAIL_TEMPLATE_PERMISSION.ADMIN_EMAIL_TEMPLATE_READ,
    EMAIL_TEMPLATE_PERMISSION.ADMIN_EMAIL_TEMPLATE_WRITE,
  ]);
  const canWrite = useHasPermission([EMAIL_TEMPLATE_PERMISSION.ADMIN_EMAIL_TEMPLATE_WRITE]);

  const navigate = useNavigate();

  const [filters, setFilters] = useState<EmailTemplateFilterDto>({
    template_type: undefined,
    search: '',
    page: 1,
    limit: 10
  });

  const { data: paginatedData, isLoading, error } = useQuery({
    queryKey: ['emailTemplates', filters],
    queryFn: () => emailTemplateService.getAllTemplates(filters),
  });

  // Fetch all available template types from API
  const { data: templateTypes = [] } = useQuery({
    queryKey: ['emailTemplateTypes'],
    queryFn: () => emailTemplateService.getTemplateTypes(),
    staleTime: 10 * 60 * 1000,
  });

  const templates = paginatedData?.templates || [];
  const totalItems = paginatedData?.total || 0;
  const totalPages = paginatedData?.total_pages || 1;

  // Build type filter options from API
  const templateTypeOptions: SelectOption[] = useMemo(() => [
    { label: 'All Types', value: 'all' },
    ...templateTypes.map(t => ({ label: t.label, value: t.value })),
  ], [templateTypes]);

  // Lookup map for displaying type labels in the table
  const typeLabelsMap = new Map(templateTypes.map(t => [t.value, t.label]));
  const getTypeLabel = (type: string): string => {
    return typeLabelsMap.get(type)
      || type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleEdit = (id: string) => {
    navigate(`/admin/enterprise/email-templates/${id}/visual-editor`);
  };

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleTypeFilterChange = (value: string | number | null) => {
    setFilters(prev => ({
      ...prev,
      template_type: value === 'all' || !value ? undefined : String(value),
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 1 }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Email Templates</h1>
            <p className="text-purple-100">
              Manage email templates for system notifications and communications
            </p>
          </div>
          <div className="bg-white/20 p-3 rounded-lg">
            <Mail className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SearchInputDebounced
            value={filters.search}
            onChange={handleSearchChange}
            placeholder="Search by name..."
            debounceDelay={500}
            loading={isLoading && !!filters.search}
          />

          <SimpleSelect
            value={filters.template_type || 'all'}
            onChange={handleTypeFilterChange}
            options={templateTypeOptions}
            placeholder="Select Type"
            isSearchable={false}
          />
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {isLoading ? 'Loading...' : `${totalItems} template${totalItems !== 1 ? 's' : ''} found`}
        </span>
      </div>

      {/* Templates Table */}
      {isLoading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Failed to load email templates</p>
          <p className="text-red-600 text-sm mt-1">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600">
            {filters.search || filters.template_type
              ? 'Try adjusting your filters'
              : 'No email templates available'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Template
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templates.map((template) => (
                    <tr key={template._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-5 h-5 text-purple-600" />
                            </div>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{template.template_name}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{template.subject_template}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full bg-purple-50 text-purple-700">
                          {getTypeLabel(template.template_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {formatDate(template.updated_at, template.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => handleEdit(template._id)}
                          disabled={!canWrite}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={!canWrite ? 'You do not have permission to edit' : 'Edit template'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && templates.length > 0 && (
        <Pagination
          currentPage={filters.page || 1}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.limit || 10}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          showItemsPerPage={true}
          itemsPerPageOptions={[10, 20, 50, 100]}
        />
      )}
    </div>
  );
}
